import { UseQueryOptions } from '@tanstack/react-query';
import useAppQuery from 'hooks/useAppQuery';
import {
  InternalIntegrationSetting,
  PropertySettingsResponseTO,
} from 'models/InternalIntegrationSetting';
import API from 'services/API';
import { INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY } from '../pages/channels/integrationSettingsQuery';

type ReactQueryOptions = Omit<
  UseQueryOptions<PropertySettingsResponseTO>,
  'queryKey' | 'queryFn'
>;

type UseFetchIntegrationPropertySettingsArgs = {
  enumId: InternalIntegrationSetting['enumId'];
  propertyUids: string[];
} & ReactQueryOptions;

export const INTEGRATION_PROPERTY_SETTINGS_QUERY_KEY =
  'integration-property-settings';

export const getIntegrationPropertySettingsQueryKey = ({
  enumId,
  propertyUids,
}: {
  enumId: InternalIntegrationSetting['enumId'];
  propertyUids: string[];
}) => [
  INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY,
  INTEGRATION_PROPERTY_SETTINGS_QUERY_KEY,
  'detail',
  { enumId, propertyUids },
];

export const fetchIntegrationPropertySettings = async (
  enumId: InternalIntegrationSetting['enumId'],
  propertyUids: string[],
) => {
  const response = await API.get<PropertySettingsResponseTO>(
    `/api/internal/integrations/default/${enumId}/property-settings`,
    {
      params: {
        propertyUids: propertyUids.join(','),
      },
    },
  );

  return response.data;
};

const useFetchIntegrationPropertySettings = ({
  enumId,
  enabled = true,
  propertyUids,
  ...options
}: UseFetchIntegrationPropertySettingsArgs) => {
  return useAppQuery<PropertySettingsResponseTO>({
    queryKey: getIntegrationPropertySettingsQueryKey({ enumId, propertyUids }),
    queryFn: () => fetchIntegrationPropertySettings(enumId, propertyUids),
    enabled,
    staleTime: 30000,
    ...(options ?? {}),
  });
};

export default useFetchIntegrationPropertySettings;
